<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">首页轮播图管理</h2>
          <p class="text-sm text-neutral-500 mt-1">
            管理首页展示的轮播图片，启用的图片数量限制在 2-5 张
            <span
              class="ml-2 px-2 py-0.5 rounded-full text-xs"
              :class="enabledCount >= 2 && enabledCount <= 5
                ? 'bg-green-100 text-green-700'
                : 'bg-orange-100 text-orange-600'"
            >
              当前启用 {{ enabledCount }} / 5 张
            </span>
          </p>
        </div>
        <div class="flex gap-3">
          <button
            @click="refreshList"
            class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
            title="刷新列表"
          >
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          </button>
          <button
            @click="openAddDialog"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <i class="fa fa-plus"></i>新增轮播图
          </button>
        </div>
      </div>

      <!-- 轮播图列表（两列并排） -->
      <div v-loading="loading" class="space-y-4">
        <div v-if="bannerList.length > 0" class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div
            v-for="banner in bannerList"
            :key="banner.id"
            class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 hover:shadow-md transition-shadow duration-200"
          >
            <div class="flex flex-col sm:flex-row gap-6">
              <!-- 缩略图 -->
              <div class="w-full sm:w-64 h-36 rounded-lg overflow-hidden bg-neutral-200 flex-shrink-0">
                <img
                  :src="banner.image"
                  :alt="banner.title || '轮播图'"
                  class="w-full h-full object-cover"
                />
              </div>

              <!-- 信息 -->
              <div class="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <div class="flex justify-between items-start mb-2">
                    <h3 class="text-lg font-bold text-neutral-900 truncate">
                      {{ banner.title || "（无标题）" }}
                    </h3>
                    <span
                      class="text-xs px-2 py-1 rounded-full whitespace-nowrap"
                      :class="{
                        'bg-green-100 text-green-700': banner.status === 1,
                        'bg-neutral-200 text-neutral-500': banner.status !== 1,
                      }"
                    >
                      {{ banner.status === 1 ? "启用" : "停用" }}
                    </span>
                  </div>
                  <div
                    v-if="banner.link"
                    class="flex items-center text-sm text-neutral-500 mb-2"
                  >
                    <i class="fa fa-link mr-1"></i>
                    <span class="truncate">{{ banner.link }}</span>
                  </div>
                  <div class="flex items-center text-sm text-neutral-500">
                    <span class="mr-4">
                      <i class="fa fa-sort-numeric-asc mr-1"></i>
                      排序: {{ banner.orderNum }}
                    </span>
                    <span>
                      <i class="fa fa-clock-o mr-1"></i>
                      {{ formatDate(banner.createTime) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 操作 -->
              <div class="flex sm:flex-col justify-end sm:justify-center gap-3 border-t sm:border-t-0 sm:border-l border-neutral-200 pt-4 sm:pt-0 sm:pl-6">
                <button
                  @click="toggleStatus(banner)"
                  class="flex-1 sm:flex-none px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center"
                  :class="banner.status === 1
                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                    : 'bg-green-50 text-green-600 hover:bg-green-100'"
                >
                  <i class="fa mr-2" :class="banner.status === 1 ? 'fa-ban' : 'fa-check'"></i>
                  {{ banner.status === 1 ? "停用" : "启用" }}
                </button>
                <button
                  @click="openEditDialog(banner)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-edit mr-2"></i>替换图片
                </button>
                <button
                  @click="deleteBanner(banner)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-neutral-50 text-neutral-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-trash-o mr-2"></i>删除
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div
          v-else
          class="bg-white rounded-xl border border-neutral-200 p-16 text-center"
        >
          <div class="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6 text-neutral-400">
            <i class="fa fa-image text-3xl"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-900 mb-2">暂无轮播图</h3>
          <p class="text-neutral-500 text-sm mb-6">
            还没有添加首页轮播图，点击上方按钮上传（启用数量需 2-5 张）
          </p>
          <button
            @click="openAddDialog"
            class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            新增轮播图
          </button>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑轮播图' : '新增轮播图'"
      width="520px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="space-y-4">
        <!-- 图片上传 -->
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            轮播图片 <span class="text-red-500">*</span>
          </label>
          <div class="flex items-start gap-4">
            <div
              v-if="form.image"
              class="w-48 h-28 rounded-lg overflow-hidden bg-neutral-100 flex-shrink-0 border border-neutral-200"
            >
              <img
                :src="form.image"
                class="w-full h-full object-cover"
                alt="轮播图预览"
              />
            </div>
            <div class="flex flex-col gap-2">
              <button
                @click="triggerUpload"
                :disabled="uploading"
                class="px-4 py-2 rounded-lg border border-primary text-primary hover:bg-primary/5 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-60"
              >
                <i class="fa" :class="uploading ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
                {{ uploading ? "上传中..." : form.image ? "重新上传" : "选择图片" }}
              </button>
              <p class="text-xs text-neutral-400">
                建议尺寸 600x400 以上，大小不超过 {{ uploadCfg.imageMaxMB }}MB
              </p>
            </div>
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleFileChange"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">标题（可选）</label>
          <input
            v-model="form.title"
            type="text"
            placeholder="请输入图片标题"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">跳转链接（可选）</label>
          <input
            v-model="form.link"
            type="text"
            placeholder="点击轮播图后跳转的地址，如 https://example.com"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">排序号</label>
            <input
              v-model.number="form.orderNum"
              type="number"
              placeholder="数字越小排序越靠前"
              class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">状态</label>
            <select
              v-model="form.status"
              class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            >
              <option :value="1">启用</option>
              <option :value="0">停用</option>
            </select>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            @click="dialogVisible = false"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors text-sm"
          >
            取消
          </button>
          <button
            @click="saveBanner"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            确定
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessageBox, ElMessage } from "element-plus";
import { imageMaxBytes, getUploadConfig } from "@/utils/uploadConfig";

export default {
  name: "BannerManager",
  data() {
    return {
      bannerList: [],
      loading: false,
      uploading: false,
      dialogVisible: false,
      isEdit: false,
      form: {
        id: "",
        title: "",
        image: "",
        link: "",
        orderNum: 0,
        status: 1,
      },
    };
  },
  computed: {
    uploadCfg() {
      return getUploadConfig();
    },
    enabledCount() {
      return this.bannerList.filter((b) => b.status === 1).length;
    },
  },
  created() {
    this.getBannerList();
  },
  methods: {
    async getBannerList() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({});
        const res = await this.$axios({
          url: "banner/admin/list",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.bannerList = res.data || [];
        }
      } catch (error) {
        console.error("获取轮播图列表失败:", error);
        this.bannerList = [];
      } finally {
        this.loading = false;
      }
    },

    refreshList() {
      this.getBannerList();
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    openAddDialog() {
      this.isEdit = false;
      this.form = {
        id: "",
        title: "",
        image: "",
        link: "",
        orderNum: (this.bannerList.length + 1) * 10,
        status: 1,
      };
      this.dialogVisible = true;
    },

    openEditDialog(banner) {
      this.isEdit = true;
      this.form = {
        id: banner.id,
        title: banner.title || "",
        image: banner.image,
        link: banner.link || "",
        orderNum: banner.orderNum || 0,
        status: banner.status,
      };
      this.dialogVisible = true;
    },

    triggerUpload() {
      this.$refs.fileInput.click();
    },

    async handleFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > imageMaxBytes()) {
        ElMessage.warning("图片大小不能超过" + getUploadConfig().imageMaxMB + "MB");
        e.target.value = "";
        return;
      }

      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("UploadType", "Files");
        formData.append(
          "businessType",
          "banner/" + new Date().format("yyyy/MM/dd")
        );

        const res = await this.$axios({
          url: "upload",
          method: "POST",
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (res.code === 200) {
          this.form.image = res.data.uploads.files[0].o.fullPath;
          ElMessage.success("图片上传成功");
        } else {
          ElMessage.error(res.message || res.msg || "图片上传失败");
        }
      } catch (error) {
        console.error("图片上传失败:", error);
        ElMessage.error("图片上传失败，请检查网络");
      } finally {
        this.uploading = false;
        e.target.value = "";
      }
    },

    async saveBanner() {
      if (!this.form.image) {
        ElMessage.warning("请先上传轮播图片");
        return;
      }
      if (this.form.link && !/^https?:\/\//.test(this.form.link.trim())) {
        ElMessage.warning("跳转链接需以 http:// 或 https:// 开头");
        return;
      }
      // 前端预校验启用数量
      if (this.form.status === 1) {
        const others = this.bannerList.filter(
          (b) => b.status === 1 && b.id !== this.form.id
        ).length;
        if (others + 1 > 5) {
          ElMessage.warning("启用的轮播图最多5张，请先停用部分图片");
          return;
        }
      }

      try {
        const payload = {
          title: (this.form.title || "").trim(),
          image: this.form.image,
          link: (this.form.link || "").trim(),
          orderNum: this.form.orderNum || 0,
          status: this.form.status,
        };
        if (this.isEdit) {
          payload.id = this.form.id;
        }

        const data = await this.$aesEncrypt(payload);
        const res = await this.$axios({
          url: "banner",
          method: "PUT",
          data: { data },
        });

        if (res.code === 200) {
          ElMessage.success(this.isEdit ? "编辑成功" : "新增成功");
          this.dialogVisible = false;
          this.getBannerList();
        } else {
          ElMessage.error(res.msg || (this.isEdit ? "编辑失败" : "新增失败"));
        }
      } catch (error) {
        console.error("保存轮播图失败:", error);
        ElMessage.error("保存失败，请检查网络");
      }
    },

    toggleStatus(banner) {
      const newStatus = banner.status === 1 ? 0 : 1;
      const actionText = newStatus === 1 ? "启用" : "停用";
      // 前端预校验
      if (newStatus === 0) {
        const remain = this.bannerList.filter(
          (b) => b.status === 1 && b.id !== banner.id
        ).length;
        if (remain > 0 && remain < 2) {
          ElMessage.warning("启用的轮播图不能少于2张");
          return;
        }
      } else {
        const others = this.bannerList.filter(
          (b) => b.status === 1 && b.id !== banner.id
        ).length;
        if (others + 1 > 5) {
          ElMessage.warning("启用的轮播图最多5张");
          return;
        }
      }

      ElMessageBox.confirm(
        `确定${actionText}轮播图"${banner.title || "未命名"}"吗？`,
        `${actionText}轮播图`,
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      ).then(async () => {
        try {
          const data = await this.$aesEncrypt({
            ...banner,
            status: newStatus,
          });
          const res = await this.$axios({
            url: "banner",
            method: "PUT",
            data: { data },
          });
          if (res.code === 200) {
            ElMessage.success(`${actionText}成功`);
            this.getBannerList();
          } else {
            ElMessage.error(res.msg || "操作失败");
          }
        } catch (error) {
          console.error("切换状态失败:", error);
          ElMessage.error("操作失败，请检查网络");
        }
      });
    },

    deleteBanner(banner) {
      if (banner.status === 1) {
        const remain = this.bannerList.filter(
          (b) => b.status === 1 && b.id !== banner.id
        ).length;
        if (remain > 0 && remain < 2) {
          ElMessage.warning("启用的轮播图不能少于2张，请先补充其他图片");
          return;
        }
      }

      ElMessageBox.confirm(
        `确定删除轮播图"${banner.title || "未命名"}"吗？删除后不可恢复`,
        "删除轮播图",
        {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "error",
        }
      ).then(() => {
        this.$axios({
          url: `banner/${banner.id}`,
          method: "DELETE",
        }).then((res) => {
          if (res.code === 200) {
            ElMessage.success("删除成功");
            this.getBannerList();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        });
      });
    },
  },
};
</script>
